import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react"
import styles from "./HeroBlockSection.module.scss"
import { Box, ScrollDown } from "@kohler-dsm-library/kohler-core"
import { MemoizedHeroBlockVideo as HeroBlockVideo } from "@/components/Remedy/components/HeroBlockSection/components/HeroBlockVideo/HeroBlockVideo"
import { HeroBlockText } from "@/components/Remedy/components/HeroBlockSection/components"
import { HeroBlockTextUnder } from "@/components/Remedy/components/HeroBlockSection/components/HeroBlockTextUnder/HeroBlockTextUnder"

/**
 * Smoothly scrolls the window to a specific Y position over a given duration.
 * @param {number} targetY - The target Y position to scroll to.
 * @param {number} duration - The duration of the scroll animation in milliseconds.
 */
function scrollToY(targetY, duration) {
  const startY = window.scrollY
  const distance = targetY - startY
  const startTime = performance.now()

  /**
   * Performs a single step of the scroll animation.
   * @param {number} currentTime - The current time in milliseconds.
   */
  function step(currentTime) {
    const elapsedTime = currentTime - startTime
    const progress = Math.min(elapsedTime / duration, 1)

    window.scrollTo(0, startY + distance * progress)

    if (progress < 1) {
      requestAnimationFrame(step)
    }
  }

  requestAnimationFrame(step)
}

export const HeroBlockSection = ({ pdpUrl = "", ctaTitle = "" }) => {
  const [shouldTriggerDive, setShouldTriggerDive] = useState(false)
  const [isScrollInitiated, setIsScrollInitiated] = useState(false)
  const sectionRef = useRef(null)

  const [isLoopingVideoLoaded, setIsLoopingVideoLoaded] = useState(false)
  const [isAnimatedVideoLoaded, setIsAnimatedVideoLoaded] = useState(false)

  const initiateAnimation = () => {
    setShouldTriggerDive(true)

    if (sectionRef.current) {
      const sectionRect = sectionRef.current.getBoundingClientRect()
      scrollToY(sectionRect.height / 2 + sectionRect.top, 2000)
    } else {
      scrollToY(window.innerHeight + 50, 2000)
    }

    setTimeout(() => {
      setIsScrollInitiated(true)
    }, 2000)
  }

  const touchMoveListener = () => {
    if (!isLoopingVideoLoaded || !isAnimatedVideoLoaded) {
      return
    }

    removeListeners()
    initiateAnimation()
  }

  const wheelListener = () => {
    if (!isLoopingVideoLoaded || !isAnimatedVideoLoaded) {
      return
    }

    removeListeners()
    initiateAnimation()
  }

  const scrollListener = () => {
    if (!isLoopingVideoLoaded || !isAnimatedVideoLoaded) {
      return
    }

    removeListeners()
    initiateAnimation()
  }

  const scrollDownClick = () => {
    if (!isLoopingVideoLoaded || !isAnimatedVideoLoaded) {
      return
    }

    removeListeners()
    initiateAnimation()
  }

  const removeListeners = () => {
    window.removeEventListener("touchmove", touchMoveListener)
    window.removeEventListener("wheel", wheelListener)
    window.removeEventListener("scroll", scrollListener)
  }

  const bindListeners = () => {
    window.addEventListener("touchmove", touchMoveListener)
    window.addEventListener("wheel", wheelListener)
    window.addEventListener("scroll", scrollListener)
  }

  useEffect(() => {
    bindListeners()
    return () => {
      removeListeners()
    }
  }, [isLoopingVideoLoaded, isAnimatedVideoLoaded])

  const handleStateUpdate = useCallback((stateFuncFor = "", value = true) => {
    if (stateFuncFor === "animation") setIsAnimatedVideoLoaded(value)
    else setIsLoopingVideoLoaded(value)
  }, [])

  return (
    <Box
      id="hero-block"
      component="section"
      mod="black"
      className={styles["pxp-hero-block"]}
      ref={sectionRef}
    >
      {isAnimatedVideoLoaded && isLoopingVideoLoaded && !shouldTriggerDive && (
        <button
          className={styles["pxp-hero-scroll-down-container"]}
          tabIndex="0"
          onClick={scrollDownClick}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              scrollDownClick()
            }
          }}
        >
          <ScrollDown />
        </button>
      )}

      {!isScrollInitiated ? (
        <style
          dangerouslySetInnerHTML={{
            __html: `
        html {
        overflow: hidden;
        scroll-behavior: auto !important;
        }
        `,
          }}
        />
      ) : (
        <style
          dangerouslySetInnerHTML={{
            __html: `
        #remedy-page {
        overflow: hidden;
        }
        `,
          }}
        />
      )}
      <HeroBlockVideo
        shouldTriggerDive={shouldTriggerDive}
        isAnimatedVideoLoaded={isAnimatedVideoLoaded}
        isLoopingVideoLoaded={isLoopingVideoLoaded}
        handleStateUpdate={handleStateUpdate}
      />
      {isAnimatedVideoLoaded && isLoopingVideoLoaded && (
        <Fragment>
          <HeroBlockText pdpUrl={pdpUrl} ctaTitle={ctaTitle} />
          <HeroBlockTextUnder />
        </Fragment>
      )}
    </Box>
  )
}
